import React from 'react';
import { MessageCircle, Zap } from 'lucide-react';

/**
 * Chat interface component for the AI book assistant
 */
const ChatInterface = ({ messages, chatInput, setChatInput, handleChatSubmit }) => (
  <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 py-8">
    <div className="max-w-4xl mx-auto px-6">
      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Chat Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6">
          <div className="flex items-center space-x-4">
            <div className="bg-white/20 p-3 rounded-full">
              <MessageCircle className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">AI Book Assistant</h2>
              <p className="text-blue-100">Your personal reading companion</p>
            </div>
          </div>
        </div>
        
        {/* Chat Messages */}
        <div className="h-96 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-12">
              <div className="bg-gradient-to-r from-purple-100 to-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-10 h-10 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Welcome to BookifyAI! ✨</h3>
              <p className="text-gray-600 mb-6">I'm here to help you discover and order amazing books!</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white p-4 rounded-xl">
                  <p className="font-semibold mb-1">Try saying:</p>
                  <p className="text-sm opacity-90">"I want 2 copies of Harry Potter"</p>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-teal-500 text-white p-4 rounded-xl">
                  <p className="font-semibold mb-1">Or ask:</p>
                  <p className="text-sm opacity-90">"Recommend me a good sci-fi book"</p>
                </div>
              </div>
            </div>
          )}
          
          {messages.map(message => (
            <div key={message.id} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl ${
                message.sender === 'user' 
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' 
                  : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800'
              } shadow-lg`}>
                <p className="text-sm leading-relaxed whitespace-pre-line">{message.text}</p>
                
                {/* Order Actions */}
                {message.action === 'order' && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button 
                      onClick={() => handleChatSubmit('Yes, add to cart')}
                      className="bg-green-500 text-white px-3 py-1 rounded-full text-xs hover:bg-green-600 transition-colors"
                    >
                      Add to Cart
                    </button>
                    <button 
                      onClick={() => handleChatSubmit('No, thanks')}
                      className="bg-gray-500 text-white px-3 py-1 rounded-full text-xs hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {/* Price Range Selection */}
                {message.action === 'price_range' && message.data?.ranges && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {message.data.ranges.map(range => (
                      <button
                        key={range.name}
                        onClick={() => handleChatSubmit(`Show books ${range.name}`)}
                        className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs hover:bg-blue-600 transition-colors"
                      >
                        {range.name} ({range.count})
                      </button>
                    ))}
                  </div>
                )}

                {/* Book Series */}
                {message.action === 'series' && message.data?.series && (
                  <div className="mt-2 space-y-2">
                    <p className="text-sm font-semibold">Books in the series:</p>
                    <div className="flex flex-wrap gap-2">
                      {message.data.series.map(book => (
                        <button
                          key={book.id}
                          onClick={() => handleChatSubmit(`Tell me about ${book.title}`)}
                          className="bg-purple-500 text-white px-3 py-1 rounded-full text-xs hover:bg-purple-600 transition-colors"
                        >
                          {book.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Author's Other Books */}
                {message.action === 'author_books' && message.data?.books && (
                  <div className="mt-2 space-y-2">
                    <p className="text-sm font-semibold">More by this author:</p>
                    <div className="flex flex-wrap gap-2">
                      {message.data.books.map(book => (
                        <button
                          key={book.id}
                          onClick={() => handleChatSubmit(`Tell me about ${book.title}`)}
                          className="bg-indigo-500 text-white px-3 py-1 rounded-full text-xs hover:bg-indigo-600 transition-colors"
                        >
                          {book.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                {message.action === 'book_details' && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      onClick={() => handleChatSubmit(`Show more by ${message.data.book.author}`)}
                      className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs hover:bg-blue-600 transition-colors"
                    >
                      More by Author
                    </button>
                    <button
                      onClick={() => handleChatSubmit(`Find similar books`)}
                      className="bg-purple-500 text-white px-3 py-1 rounded-full text-xs hover:bg-purple-600 transition-colors"
                    >
                      Similar Books
                    </button>
                    <button
                      onClick={() => handleChatSubmit(`Add "${message.data.book.title}" to cart`)}
                      className="bg-green-500 text-white px-3 py-1 rounded-full text-xs hover:bg-green-600 transition-colors"
                    >
                      Add to Cart
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {/* Chat Input */}
        <div className="p-6 bg-gray-50 border-t">
          <div className="flex space-x-3">
            <input
              type="text"
              placeholder="Ask me about books or place an order..."
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleChatSubmit()}
              className="flex-1 p-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-purple-200 focus:border-purple-400 transition-all"
            />
            <button
              onClick={handleChatSubmit}
              className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white px-8 py-4 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-2"
            >
              <span>Send</span>
              <MessageCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default ChatInterface;
